import type {
  ColourStop,
  SchematicAnim,
  SchematicAnimTarget,
  SchematicOverlayWidget,
} from '../../../widgets';
import { HmiAssetPicker } from '../../../../components/HmiAssetPicker/HmiAssetPicker';
import { useAsync } from '../../../../hooks/useAsync';
import {
  BindingRow,
  ColourField,
  Field,
  ListEditor,
  NumberField,
  Row,
  ScalarField,
  Section,
  SelectField,
  SuggestField,
  TextField,
  patcher,
  rangeError,
  type WidgetEditorProps,
} from '../fields';

const TARGETS: { value: SchematicAnimTarget; label: string }[] = [
  { value: 'fill', label: 'Füllfarbe' },
  { value: 'rotate', label: 'Drehung' },
  { value: 'visible', label: 'Sichtbarkeit' },
  { value: 'text', label: 'Text' },
  { value: 'pathLength', label: 'Füllstand entlang des Pfades' },
];

/**
 * Anlagenschema: Hintergrund plus Animationsliste.
 *
 * Zur Element-Id: sie per Hand eintippen zu lassen waere der schwaechste
 * denkbare Weg, ein Element-Waehler, der das SVG darstellt und anklickbar
 * macht, ein eigenes Stueck Arbeit. Der Mittelweg hier: das gewaehlte Asset
 * wird einmal geladen und mit dem DOMParser des Browsers nach Elementen mit
 * `id` durchsucht. Was dabei herauskommt, steht als Vorschlagsliste im Feld.
 * Getippt wird weiterhin von Hand, aber nicht mehr geraten, und ein Tippfehler
 * faellt sofort auf, weil der Vorschlag nicht zuklappt.
 *
 * Bewusst kein Zwang: ein Element, das erst zur Laufzeit im SVG entsteht,
 * steht in keiner Vorschlagsliste, und dann muss der Autor es trotzdem
 * eintragen koennen.
 */
export function SchematicOverlayEditor(
  { widget, plantId, missing, onChange }: WidgetEditorProps<SchematicOverlayWidget>,
) {
  const patch = patcher(widget, onChange);
  const assetId = widget.assetRef?.assetId ?? null;
  const elementIds = useSvgElementIds(assetId);

  return (
    <>
      <Section
        title="Hintergrund"
        hint="Genau eine Quelle: entweder ein hochgeladenes Asset oder die alte, projekteigene SVG-Id. Beides zugleich lehnt der Server ab."
      >
        <Field
          label="Asset"
          required
          missing={missing.has('assetRef')}
        >
          <HmiAssetPicker
            value={assetId}
            onChange={(next) => patch({
              assetRef: next ? { assetId: next } : undefined,
              // Ein gewaehltes Asset schliesst die alte SVG-Id aus, sonst
              // haette das Widget zwei Hintergruende und der Validator auf
              // der Serverseite weist es zurueck.
              svgId: next ? undefined : widget.svgId,
            })}
          />
        </Field>
        {/* Hier stand bis zum 23.09.2026 ein Feld "SVG-Id (alter Weg)". Es ist
            weg, weil es nirgendwohin fuehrte: die Route `GET /api/hmi/svg/{id}`,
            die es bedienen sollte, ist nie entstanden. Ein Eingabefeld
            anzubieten, dessen Inhalt garantiert einen 404 ergibt, ist schlechter
            als keines - der Autor sucht den Fehler dann bei sich. Wer ein altes
            Projekt mit `svgId` oeffnet, sieht den Hinweis unten und weiss, was
            zu tun ist. */}
        {!assetId && widget.svgId && (
          <p class="hv-pf__help hv-pf__help--warn">
            Dieses Widget hängt an der SVG-Id <code>{widget.svgId}</code>.
            Dieser Weg wurde nie fertig und wird nicht mehr bedient. Oben ein
            Asset wählen; die Id verschwindet dabei von selbst.
          </p>
        )}
        {assetId && elementIds.length > 0 && (
          <p class="hv-pf__help">
            {elementIds.length} benannte Elemente im Hintergrund gefunden.
          </p>
        )}
        {assetId && elementIds.length === 0 && (
          <p class="hv-pf__help">
            Im Hintergrund wurden keine benannten Elemente gefunden. Ohne
            `id`-Attribute im SVG lässt sich nichts animieren: das Schema
            muss mit Element-Ids exportiert werden.
          </p>
        )}
      </Section>

      <Section title="Animationen">
        <ListEditor<SchematicAnim>
          label="Animationen"
          items={widget.anims}
          addLabel="Animation"
          emptyHint="Ohne Animation ist das Widget ein Bild."
          create={() => ({ elementId: '', target: 'fill', valuePoint: '' })}
          itemTitle={(a, i) => a.elementId || `Animation ${i + 1}`}
          onChange={(next) => patch({ anims: next })}
          renderItem={(anim, patchAnim, index) => (
            <>
              <Row>
                <SuggestField
                  label="Element-Id"
                  value={anim.elementId}
                  keepEmptyString
                  required
                  missing={anim.elementId === ''}
                  suggestions={elementIds}
                  listId={`schematic-el-${widget.id}-${index}`}
                  placeholder="valve_v01"
                  onChange={(next) => patchAnim({ elementId: next ?? '' })}
                />
                <SelectField
                  label="Wirkung"
                  value={anim.target}
                  options={TARGETS}
                  onChange={(next) => patchAnim({ target: next })}
                />
              </Row>
              <BindingRow
                label="Datenpunkt"
                value={anim.valuePoint}
                plantId={plantId}
                filter={numericTarget(anim.target) ? { valueType: 'number' } : undefined}
                onChange={(next) => patchAnim({ valuePoint: next })}
              />
              {numericTarget(anim.target) && (
                <Row>
                  <NumberField
                    label="Wert unten"
                    value={anim.min}
                    error={rangeError(anim.min ?? undefined, anim.max ?? undefined)}
                    hint={anim.target === 'rotate' ? 'Wird auf 0 Grad abgebildet.' : undefined}
                    onChange={(next) => patchAnim({ min: next ?? null })}
                  />
                  <NumberField
                    label="Wert oben"
                    value={anim.max}
                    hint={anim.target === 'rotate' ? 'Wird auf 360 Grad abgebildet.' : undefined}
                    onChange={(next) => patchAnim({ max: next ?? null })}
                  />
                </Row>
              )}
              {anim.target === 'visible' && (
                <ScalarField
                  label="Sichtbar, wenn der Wert gleich"
                  value={anim.visibleWhen}
                  onChange={(next) => patchAnim({ visibleWhen: next })}
                />
              )}
              {anim.target === 'text' && (
                <TextField
                  label="Textformat"
                  value={anim.textFormat}
                  placeholder="{0:0.0} Grad C"
                  hint="Platzhalter {0} steht für den Wert."
                  onChange={(next) => patchAnim({ textFormat: next })}
                />
              )}
              {anim.target === 'fill' && (
                <ListEditor<ColourStop>
                  label="Farbverlauf"
                  items={anim.colourRamp ?? []}
                  addLabel="Stützstelle"
                  emptyHint="Ohne Stützstellen bleibt die Farbe aus dem SVG stehen."
                  hint="Zwischen zwei Stützstellen wird interpoliert. Bei Aufzählungen je Wert eine Stelle setzen."
                  create={() => ({ at: 0, colour: '#8a929e' })}
                  itemTitle={(stop) => `bei ${stop.at}`}
                  onChange={(next) => patchAnim({ colourRamp: next })}
                  renderItem={(stop, patchStop) => (
                    <Row>
                      <NumberField
                        label="Bei Wert"
                        value={stop.at}
                        onChange={(next) => patchStop({ at: next ?? 0 })}
                      />
                      <ColourField
                        label="Farbe"
                        value={stop.colour}
                        onChange={(next) => patchStop({ colour: next ?? '' })}
                      />
                    </Row>
                  )}
                />
              )}
            </>
          )}
        />
      </Section>
    </>
  );
}

/** Drehung und Fuellstand brauchen eine Zahl. Farbe, Sichtbarkeit und Text
 *  kommen auch mit einer Aufzaehlung oder einem Bool zurecht. */
function numericTarget(target: SchematicAnimTarget): boolean {
  return target === 'rotate' || target === 'pathLength';
}

/**
 * Element-Ids aus dem gewaehlten Asset.
 *
 * Bewusst der DOMParser und kein SVG-Baum im Speicher: wir wollen die Namen,
 * nicht das Bild. Faellt der Abruf aus (Asset geloescht, kein SVG, sondern
 * ein PNG), bleibt die Liste leer und das Feld ein normales Textfeld. Ein
 * Fehler ist das nicht, nur eine fehlende Bequemlichkeit.
 */
function useSvgElementIds(assetId: string | null): string[] {
  const state = useAsync(async (signal) => {
    if (!assetId) return [] as string[];
    const response = await fetch(`/api/hmi-assets/${assetId}/content`, {
      signal,
      credentials: 'include',
    });
    if (!response.ok) return [] as string[];
    const type = response.headers.get('content-type') ?? '';
    if (!type.includes('svg')) return [] as string[];
    const text = await response.text();
    const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
    if (doc.querySelector('parsererror')) return [] as string[];
    const ids = [...doc.querySelectorAll('[id]')]
      .map((el) => el.id)
      .filter((id) => id.length > 0);
    return [...new Set(ids)].sort();
  }, [assetId]);
  return state.status === 'ok' ? state.data : [];
}
